import {Button, Card} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import {useHistory, useParams} from 'react-router';
import {ConfirmDialog} from '../../../base-components/StudioConfirmDialog';
import {RootState, useAppDispatch} from '../../../store';
import {
  deleteInferenceApp,
  fetchInferenceApps,
} from '../../../store/prodDeployments/dashboard';
import {Metadata, MetadataList} from '../MetadataList/MetadataList';
import {toast} from '../../../base-components/StudioToast';
import {Link} from 'react-router-dom';
import {useStudioMode} from '../../StudioMode/StudioModeProvider';
import './InferenceDetails.scss';
import {
  DeploymentPostProcessingParameters,
  PreProcessingParameters,
} from '../../TestDeployView/DeployConfigPanel/TestDeployParameters';
import {InferenceAppDetails} from '../InferenceAppDetails/InferenceAppDetails';
import {getInferenceApplication} from '../../InferenceApp/GetInferenceApplication';
import {SinglePageApplication} from '../../../types/project/Application';
import {InferenceApplicationInfo} from '../../../types/deployment/InferenceApplicationsResponse';
import {InferenceApplicationsRequest} from '../../../types/deployment/InferenceApplicationsRequest';
import cn from 'classnames';

export const InferenceDetails = () => {
  const intl = useIntl();
  const {mode} = useStudioMode();
  const isLiteMode = mode === 'LITE';
  const {projectId, applicationId, spaId} = useParams<{
    projectId: string;
    applicationId: string;
    spaId: string;
  }>();

  const [spa, setSpa] = useState<SinglePageApplication>();
  const userId = useSelector((state: RootState) => state.user.userId);
  const groupId = useSelector((state: RootState) => state.user.groupId);
  const inferenceApps: Array<InferenceApplicationInfo> = useSelector(
    (state: RootState) => state.deploymentDashboard.inferenceApps.data
  );
  const inferenceApplication = inferenceApps.find(
    app => app.applicationId === applicationId
  );

  useEffect(() => {
    async function fetchSpa() {
      const spaResponse = await getInferenceApplication(spaId, applicationId);
      if (!spaResponse) {
        console.log(intl.formatMessage({id: 'inferenceApp.error.notfound'}));
      } else {
        setSpa(spaResponse);
      }
    }
    fetchSpa();
  }, [spaId, applicationId, intl]);

  const dispatch = useAppDispatch();
  const history = useHistory();
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
  }>({isOpen: false});
  const preProcessor =
    inferenceApplication?.transformation?.transformationType === 'SCRIPT'
      ? inferenceApplication.transformation
      : null;
  const modelPostProcessor = inferenceApplication?.postProcessors?.find(
    pp => pp.postProcessorType === 'MODEL'
  );
  const appPostProcessor = inferenceApplication?.postProcessors?.find(
    pp => pp.postProcessorType === 'APPLICATION' && !pp.outOfPackage
  );
  const spaPostProcessors = inferenceApplication?.postProcessors?.filter(
    pp => pp.id === spa?.postProcessorId && pp.outOfPackage
  );

  useEffect(() => {
    if (applicationId && groupId) {
      const requestBody: InferenceApplicationsRequest = {
        firstPage: 0,
        pageSize: 2,
        paginated: false,
        orderDefinitions: [],
        params: {applicationId: applicationId},
        groupId,
      };

      dispatch(fetchInferenceApps(requestBody));
    }
  }, [projectId, applicationId, dispatch, groupId]);

  return inferenceApplication ? (
    <div className="inference-details" data-testid="inference-details">
      <div className="inference-details__menu">
        <Button
          className="prod-deploy-details__back"
          variant="contained"
          disableElevation
          onClick={() => history.goBack()}
        >
          <FormattedMessage id="form.returnToPreviousPage" />
        </Button>
        <div>
          <Button
            onClick={() => history.push(`/deployments?tab=inference`)}
            className="prod-deploy-details__back"
            variant="contained"
            disableElevation
            disabled={isLiteMode}
          >
            {intl.formatMessage({id: 'dashboard.viewAll'})}
          </Button>
          <Button
            onClick={() => {
              if (spa?.target.type === 'PRODUCTION') {
                history.push(
                  `/project/${projectId}/inference/${applicationId}/deployment/${spa
                    ?.target.deploymentId || ''}/configure?spaId=${spa?.id || ''}`
                );
              } else if (spa?.target.type === 'TEST') {
                history.push(
                  `/project/${projectId}/inference/${applicationId}/create?spaId=${spa?.id ||
                    ''}`
                );
              }
            }}
            color="primary"
            variant="contained"
            disableElevation
            disabled={userId !== inferenceApplication.ownerId}
          >
            {intl.formatMessage({id: 'form.edit'})}
          </Button>
        </div>
      </div>
      <div className="inference-details__body">
        <div className="inference-details__info">
          <div className="inference-details__header">
            <h3>{inferenceApplication.name}</h3>
            <Button
              variant="contained"
              color="secondary"
              disableElevation
              onClick={() => setDeleteConfirmation({isOpen: true})}
              disabled={userId !== inferenceApplication?.ownerId}
            >
              {intl.formatMessage({id: 'form.delete'})}
            </Button>
          </div>
          <MetadataList>
            <Metadata label="prodDeployment.project">
              {inferenceApplication.projectName}
            </Metadata>
            <Metadata label="prodDeployment.dataset">
              {inferenceApplication.datasetName}
            </Metadata>
            <Metadata label="prodDeployment.model">
              {inferenceApplication.modelName}
            </Metadata>
            {spa && (
              <InferenceAppDetails
                spa={spa}
                showLinkToEdit={userId === inferenceApplication.ownerId}
                showActionButtons={false}
              />
            )}
            <Metadata label="prodDeployment.preProcessor">
              {preProcessor && (
                <Button
                  color="primary"
                  className={cn('processor-name')}
                  disabled={userId !== inferenceApplication.ownerId}
                >
                  <Link
                    to={`/project/${projectId}/preprocessor/edit/${
                      preProcessor.id
                    }?applicationId=${applicationId}&spaId=${spa?.id ||
                      ''}&returnTo=spaDetails&viewOnly`}
                  >
                    {preProcessor.name}
                  </Link>
                </Button>
              )}
            </Metadata>
            <Metadata label="prodDeployment.modelPostProcessor">
              {modelPostProcessor && (
                <Button
                  color="primary"
                  className={cn('processor-name')}
                  disabled={userId !== inferenceApplication.ownerId}
                >
                  <Link
                    to={`/project/${projectId}/postprocessor/edit/${
                      modelPostProcessor.id
                    }?applicationId=${applicationId}&spaId=${spa?.id ||
                      ''}&returnTo=spaDetails&viewOnly`}
                  >
                    {modelPostProcessor.name}
                  </Link>
                </Button>
              )}
            </Metadata>
            <Metadata label="prodDeployment.appPostProcessor">
              {appPostProcessor && (
                <Button
                  color="primary"
                  className={cn('processor-name')}
                  disabled={userId !== inferenceApplication.ownerId}
                >
                  <Link
                    to={`/project/${projectId}/postprocessor/edit/${
                      appPostProcessor.id
                    }?applicationId=${applicationId}&spaId=${spa?.id ||
                      ''}&returnTo=spaDetails&viewOnly`}
                  >
                    {appPostProcessor.name}
                  </Link>
                </Button>
              )}
            </Metadata>
            {spaPostProcessors &&
              spaPostProcessors.map((spaPostProcessor, i) => {
                return (
                  <Metadata label="prodDeployment.spaPostProcessors" key={`spa-m-${i}`}>
                    <Button
                      color="primary"
                      className={cn('processor-name')}
                      disabled={userId !== inferenceApplication.ownerId}
                    >
                      <Link
                        key={`spa-pp-${i}`}
                        to={`/project/${projectId}/postprocessor/edit/${
                          spaPostProcessor.id
                        }?applicationId=${applicationId}&spaId=${spa?.id ||
                          ''}&returnTo=spaDetails&viewOnly`}
                      >
                        {spaPostProcessor.name}
                      </Link>
                    </Button>
                    <br />
                  </Metadata>
                );
              })}
            {projectId && (
              <Metadata label="prodDeployment.parameters">
                <PreProcessingParameters
                  projectId={projectId}
                  applicationId={applicationId}
                  renderField={(field, param) => (
                    <div className="inference-details__param" key={param.id}>
                      {field}
                    </div>
                  )}
                  titleMessageId="test.form.preprocessing"
                  wrapperClassName="inference-details__accordion"
                  detailsClassName="deploy-settings__body_container"
                  onValidityChange={() => {}}
                  onChange={() => {}}
                  disabledForm={true}
                />
                <DeploymentPostProcessingParameters
                  projectId={projectId}
                  applicationId={applicationId}
                  spaId={spa?.id}
                  renderField={(field, param) => (
                    <div className="inference-details__param" key={param.id}>
                      {field}
                    </div>
                  )}
                  titleMessageId="test.form.postprocessing"
                  wrapperClassName="inference-details__accordion"
                  detailsClassName="deploy-settings__body_container"
                  onValidityChange={() => {}}
                  onChange={() => {}}
                  disabledForm={true}
                />
              </Metadata>
            )}
          </MetadataList>
        </div>
        <Card className="inference-details__results">
          <div id="inference-results-placeholder"></div>
        </Card>
      </div>
      <ConfirmDialog
        type="confirm"
        title={intl.formatMessage({id: 'prodDeployment.deleteAppConfirmTitle'})}
        message={intl.formatMessage({id: 'prodDeployment.deleteAppConfirmMessage'})}
        submitLabel={intl.formatMessage({
          id: 'prodDeployment.deleteConfirmSubmitLabel',
        })}
        open={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({isOpen: false})}
        onOk={async () => {
          await dispatch(deleteInferenceApp({applicationId, spaId: spa?.id}));
          setDeleteConfirmation({isOpen: false});
          toast.success(`Successfully deleted application`);
          history.push(`/deployments?tab=inference`);
        }}
      />
    </div>
  ) : null;
};
